import { useCallback, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { DrawerForm } from "../../Common/styles/drawer";
import { useDropzone } from "react-dropzone";
import { Add, Delete } from "@mui/icons-material";
import { studyParticipantDocumentUploadUrl } from "../../../utils/upload";
import http from "../../../utils/http";
import {
  previewWrapper,
  uploadWrapper,
} from "../../QuestionBuilder/QuestionProperties/BasicTab";
import { Avatar, IconButton, Stack } from "@mui/material";
import { useParams } from "react-router-dom";

const UploadItem: React.FC<any> = ({
  file,
  setFieldValue,
  studyId,
  participantId,
}) => {
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: any) => {
      try {
        const file = acceptedFiles?.[0];
        if (file) {
          if (file.size > 10 * 1024 * 1024) {
            toastMessage("warning", "File Size cannot be greater than 10 MB!");
            return;
          }
          setLoading(true);
          const url = await studyParticipantDocumentUploadUrl(
            file,
            studyId,
            participantId
          );
          if (url) {
            const obj = {
              previewUrl: url?.previewUrl,
              url: url?.postUploadImageUrl,
              name: file.name,
              type: file.type,
            };
            setFieldValue("file", obj);
          }
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    },
    [setFieldValue, studyId, participantId]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/vnd.ms-powerpoint": [],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
    },
  });

  const removeFile = () => {
    setFieldValue("file", null);
  };

  const isImage = file?.type?.startsWith("image/");

  return (
    <Stack gap={1}>
      {loading ? (
        <Box sx={{ color: "text.secondary" }}>
          <CircularProgress />
        </Box>
      ) : file ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box {...getRootProps({ className: "dropzone" })} sx={previewWrapper}>
            <input {...getInputProps()} />
            {isImage ? (
              <Avatar
                src={file.previewUrl}
                variant="rounded"
                sx={{ width: 200, height: 140 }}
              />
            ) : (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "regular",
                  color: "text.secondary",
                }}
              >
                {file.name}
              </Typography>
            )}
          </Box>
          <IconButton
            color={"error"}
            sx={{ height: 40, width: 40 }}
            onClick={removeFile}
          >
            <Delete />
          </IconButton>
        </Box>
      ) : (
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={1}
          {...getRootProps({ className: "dropzone" })}
          sx={uploadWrapper}
        >
          <input {...getInputProps()} />

          <Stack alignItems={"center"} gap={1}>
            <Add fontSize="medium" sx={{ color: "text.secondary" }} />
            <Typography
              sx={{ color: "text.secondary" }}
              variant="body1"
              fontWeight={"regular"}
              textAlign={"center"}
            >
              Add New Version <br />
              <Typography
                sx={{ color: "text.secondary" }}
                component={"span"}
                variant="body1"
                fontWeight={"regular"}
              >
                or drag and drop
              </Typography>
            </Typography>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const schema = yup.object().shape({
  file: yup.mixed().required("File is Required"),
});

type Props = {
  showModal: boolean;
  closeModal: () => void;
  documentId: string;
  documentName: string;
  documentType: string;
  documentCategory: string;
  onVersionAdded: () => void;
};

const AddVersionModal = ({
  showModal,
  closeModal,
  documentId,
  documentName,
  documentType,
  documentCategory,
  onVersionAdded,
}: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const { id: studyId, participantId } = useParams();
  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);

      const body: any = {
        url: values?.file?.url,
        participantId,
      };

      const res = await http.post(
        `/participant-documents/study/${studyId}/${documentId}/versions`,
        body
      );
      toastMessage(
        "success",
        res?.data?.message || "New version added successfully"
      );
      closeModal();
      setSubmitLoader(false);
      onVersionAdded();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title="Add New Version" onCloseClick={closeModal} />
        <Formik
          initialValues={{
            file: null,
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <Box sx={{ mb: 3, display: "flex", gap: 4 }}>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={"regular"}
                    sx={{ mb: 1 }}
                  >
                    Document Name
                  </Typography>
                  <Typography variant="subtitle1">
                    {documentName || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={"regular"}
                    sx={{ mb: 1 }}
                  >
                    Document Type
                  </Typography>
                  <Typography variant="subtitle1">
                    {documentType || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={"regular"}
                    sx={{ mb: 1 }}
                  >
                    Document Category
                  </Typography>
                  <Typography variant="subtitle1" textTransform="capitalize">
                    {documentCategory || "-"}
                  </Typography>
                </Box>
              </Box>

              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle}>
                  Upload New Version <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <UploadItem
                  file={values?.file}
                  setFieldValue={setFieldValue}
                  participantId={participantId}
                  studyId={studyId}
                />
                <FormHelperText
                  error={touched?.file && errors?.file ? true : false}
                >
                  {touched?.file && errors?.file
                    ? (errors?.file as string)
                    : " "}
                </FormHelperText>
              </FormControl>

              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        Add Version
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddVersionModal;
